import dayjs from "dayjs";
import { type ComponentInfo, formatComponentLabel } from "./componentLabel";

type AssigneeInfo = {
  username: string;
  first_name?: string;
  last_name?: string;
};

export type WOClipboardContext = {
  locationName?: string | null;
  turbineSerialNumber?: string | null;
  components: ComponentInfo[];
  caseNumber?: string | null;
  deadline?: string | null;
  ticketNumber?: number | null;
  woAssignees: AssigneeInfo[];
};

type SessionClipboardParams = WOClipboardContext & {
  startedAt?: string | null;
  endedAt?: string | null;
  taskCheckin?: string | null;
  damCheckin?: string | null;
  dfCheckin?: string | null;
  weatherCheckin?: string | null;
  progressCheckin?: string | null;
  carCheckin?: string | null;
  taskCheckout?: string | null;
  damCheckout?: string | null;
  dfCheckout?: string | null;
  weatherCheckout?: string | null;
  progressCheckout?: string | null;
  turbineStoppedCheckout?: string | null;
  carCheckout?: string | null;
  // When provided (field app), the report shows the components selected at check-in
  // instead of the whole work order's component list. Left undefined by callers that
  // want the list.
  selectedComponents?: ComponentInfo[] | null;
  phase?: "checkin" | "checkout" | "full";
};

// One phase's own values; the work-order context around them is the same in both.
// The progress field is forward-looking at check-in and retrospective at check-out,
// so each phase carries the label its own form uses.
type PhaseValues = {
  dateTime: string;
  progressLabel: string;
  task?: string | null;
  dam?: string | null;
  df?: string | null;
  weather?: string | null;
  progress?: string | null;
  // Reported only by the block that closes the session, so the check-in block
  // leaves both undefined and their lines are left out of it.
  turbineStopped?: string | null;
  estimatedCompletionDate?: string | null;
  car?: string | null;
};

export function formatComponentParts(component: ComponentInfo): string {
  return [formatComponentLabel(component), f(component.model)].join(" / ");
}

// Multiple tasks can reference the same component; dedup on the rendered line keeps
// each distinct component to a single entry while preserving first-appearance order.
function pushComponentLines(lines: string[], componentList: ComponentInfo[]): void {
  const seenComponentLines = new Set<string>();
  componentList.forEach((component) => {
    const componentLine = formatComponentParts(component);
    if (seenComponentLines.has(componentLine)) return;
    seenComponentLines.add(componentLine);
    lines.push(componentLine);
  });
}

export function f(value: string | null | undefined): string {
  return value?.trim() || "—";
}

export function fmtDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  return dayjs(value).format("YYYY-MM-DD HH:mm");
}

function fmtDate(value: string | null | undefined): string {
  if (!value) return "—";
  return dayjs(value).format("YYYY-MM-DD");
}

function fmtTurbineStatus(value: string | null | undefined): string {
  if (value === "YES") return "Stopped";
  if (value === "NO") return "Not Stopped";
  return "—";
}

export function fmtName(a: AssigneeInfo): string {
  const parts = [a.first_name?.trim(), a.last_name?.trim()].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : a.username;
}

export function formatSessionClipboard(params: SessionClipboardParams): string {
  const {
    locationName, turbineSerialNumber, components, caseNumber, deadline,
    ticketNumber, woAssignees,
    startedAt, endedAt,
    taskCheckin, damCheckin, dfCheckin, weatherCheckin, progressCheckin, carCheckin,
    taskCheckout, damCheckout, dfCheckout, weatherCheckout, progressCheckout, turbineStoppedCheckout, carCheckout,
    selectedComponents,
    phase = "full",
  } = params;

  // Identical in both blocks, and the dedup only has to run once.
  const componentLines: string[] = [];
  if (selectedComponents !== undefined) {
    // Field app: show only the components the session was checked in against.
    if (selectedComponents === null || selectedComponents.length === 0) {
      componentLines.push("Component: N/A");
    } else {
      pushComponentLines(componentLines, selectedComponents);
    }
  } else if (components.length === 0) {
    componentLines.push("Component: N/A");
  } else {
    pushComponentLines(componentLines, components);
  }

  const lines: string[] = [];

  // Each phase is a self-contained block: the work-order context is repeated above
  // every phase's own values so that one block can be pasted on its own.
  const pushPhaseBlock = (marker: string, phaseValues: PhaseValues): void => {
    lines.push(marker);
    if (ticketNumber != null) lines.push(`Work Order: #${ticketNumber}`);
    lines.push(`Date/Time: ${phaseValues.dateTime}`);
    lines.push(`Site: ${f(locationName)}`);
    lines.push(`WTG: ${f(turbineSerialNumber)}`);
    lines.push(...componentLines);
    if (phaseValues.df !== undefined) lines.push(`DF: ${f(phaseValues.df)}`);
    lines.push(`Task: ${f(phaseValues.task)}`);
    lines.push(`INC: ${f(caseNumber)}`);
    lines.push(`DAM: ${f(phaseValues.dam)}`);
    lines.push(`Weather: ${f(phaseValues.weather)}`);
    lines.push(`${phaseValues.progressLabel}: ${f(phaseValues.progress)}`);
    if (phaseValues.turbineStopped !== undefined)
      lines.push(`Turbine status: ${fmtTurbineStatus(phaseValues.turbineStopped)}`);
    if (phaseValues.estimatedCompletionDate !== undefined)
      lines.push(`Estimated Comp. Date: ${fmtDate(phaseValues.estimatedCompletionDate)}`);
    lines.push(`Vehicle: ${f(phaseValues.car)}`);
    lines.push(
      `Team: ${woAssignees.length > 0 ? woAssignees.map(fmtName).join(", ") : "—"}`
    );
  };

  const emitCheckin = phase !== "checkout";
  const emitCheckout = phase !== "checkin";

  if (emitCheckin) {
    pushPhaseBlock("--- Check In ---", {
      dateTime: fmtDateTime(startedAt),
      progressLabel: "Plan For Today",
      task: taskCheckin,
      dam: damCheckin,
      df: dfCheckin,
      weather: weatherCheckin,
      progress: progressCheckin,
      car: carCheckin,
    });
  }

  if (emitCheckout) {
    pushPhaseBlock("--- Check Out ---", {
      dateTime: endedAt ? fmtDateTime(endedAt) : "Not yet checked out",
      progressLabel: "Work Done Today",
      task: taskCheckout,
      dam: damCheckout,
      df: dfCheckout,
      weather: weatherCheckout,
      progress: progressCheckout,
      turbineStopped: turbineStoppedCheckout ?? null,
      estimatedCompletionDate: deadline ?? null,
      car: carCheckout,
    });
  }

  return lines.join("\n");
}
